import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Modal, Form, Pagination } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { Get, Put } from '@app/utils/httpProvider';
import { ACCOUNT_AUTHINFO_API, ACCOUNT_API, BaseGatewayImage, DANHSACH_DUYET_NGOAINGU_API, DONVI_API, DOITUONG_API } from '@app/utils/apiUrl';
import { toast } from 'react-toastify';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import Select from 'react-select';

// Helper Format Ngày
const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('vi-VN');
};

// Helper Format Điểm Số
const formatDiemSo = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return Number(value).toLocaleString('vi-VN');
};

// ─── Helper: Tính toán mảng phân trang ────────────────────────────────────────
const getPaginationItems = (currentPage, totalPages) => {
    let items = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) items.push(i);
    } else {
        if (currentPage <= 4) {
            items = [1, 2, 3, 4, 5, '...', totalPages];
        } else if (currentPage >= totalPages - 3) {
            items = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        } else {
            items = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
        }
    }
    return items;
};

const DuyetChungChiNgoaiNgu = () => {
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = sessionStorage.getItem('ngoaingu_page');
        return savedPage ? parseInt(savedPage, 10) : 1;
    });

    useEffect(() => {
        sessionStorage.setItem('ngoaingu_page', currentPage);
    }, [currentPage]);
    const itemsPerPage = 5;
    const [selectedDonVi, setSelectedDonVi] = useState('');
    const [filterChucDanh, setFilterChucDanh] = useState('');
    const [danhSachChucDanh, setDanhSachChucDanh] = useState([]);

    // State tìm kiếm theo CCCD
    const [searchCccdInput, setSearchCccdInput] = useState('');
    const [searchCccd, setSearchCccd] = useState('');

    // State tìm kiếm theo Tên
    const [searchNameInput, setSearchNameInput] = useState('');
    const [searchName, setSearchName] = useState('');

    const [isExporting, setIsExporting] = useState(false);

    // Fetch danh sách đơn vị từ danh mục dùng chung (cực kỳ nhẹ, không ảnh hưởng performance)
    const { data: donViRes } = useQuery(['danhMucDonViAll'], async () => {
        const res = await Get(DONVI_API.GET_PAGING, null, { PageSize: 10000 });
        return res?.data || res;
    });

    const { data: chucDanhRes } = useQuery(['danhMucChucDanhAll'], async () => {
        const res = await Get(DOITUONG_API.GET_PAGING, null, { PageSize: 10000 });
        return res?.data || res;
    });

    const donViOptions = React.useMemo(() => {
        const list = Array.isArray(donViRes) ? donViRes : (donViRes?.data || []);
        return list.map(item => {
            const ten = item.tenDonVi || item.TenDonVi;
            return ten ? { value: ten, label: ten } : null;
        }).filter(Boolean).sort((a, b) => a.label.localeCompare(b.label, 'vi'));
    }, [donViRes]);

    const chucDanhOptions = React.useMemo(() => {
        const list = Array.isArray(chucDanhRes) ? chucDanhRes : (chucDanhRes?.data || []);
        return list.map(item => {
            const ten = item.tenDoiTuong || item.TenDoiTuong;
            return ten ? { value: ten, label: ten } : null;
        }).filter(Boolean);
    }, [chucDanhRes]);

    // State cho modal xác nhận duyệt
    const [openConfirmDuyet, setOpenConfirmDuyet] = useState(false);
    const [openReasonModal, setOpenReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // { item, field, value, label }

    // Hàm fetch dữ liệu và nhóm theo AccountId
    const fetchAllData = async () => {
        try {
            let accountIdToSearch = null;
            if (searchCccd) {
                try {
                    const authRes = await Get(`${ACCOUNT_AUTHINFO_API.GET_BY_USERNAME}/${searchCccd}`);
                    const authData = authRes?.data || authRes;
                    accountIdToSearch = authData?.accountId || authData?.AccountId;
                    if (!accountIdToSearch) {
                        return { items: [], totalRecords: 0 };
                    }
                } catch (e) {
                    console.error('Lỗi lấy account id theo cccd:', e);
                    return { items: [], totalRecords: 0 };
                }
            }

            let params = {
                pageIndex: currentPage,
                pageSize: itemsPerPage,
                keyword: "",
            };
            if (selectedDonVi || filterChucDanh || searchName) {
                params = { pageIndex: 1, pageSize: 1000000, keyword: "" };
            }
            if (accountIdToSearch) {
                params.accountId = accountIdToSearch;
            }

            if (searchName) {
                try {
                    const accountsRes = await Get(ACCOUNT_AUTHINFO_API.GET_PAGING, null, { keyword: searchName, email: "", pageIndex: 1, pageSize: 1000000 });
                    const accountsData = accountsRes?.data || accountsRes;
                    const matchedAccounts = accountsData?.data || accountsData?.$values || (Array.isArray(accountsData) ? accountsData : []);

                    const matchedIds = matchedAccounts.map(acc => acc.accountId || acc.AccountId).filter(Boolean);

                    if (matchedIds.length === 0) {
                        return { items: [], totalRecords: 0 };
                    }
                    params.AccountIds = matchedIds;
                } catch (e) {
                    console.error('Lỗi lấy danh sách account theo tên:', e);
                    return { items: [], totalRecords: 0 };
                }
            }

            const chungChiRes = await Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_CHUNGCHINGOAINGU_BY_PAGING, null, params);

            let chungChiItems = (chungChiRes?.data?.$values || chungChiRes?.data?.data || (Array.isArray(chungChiRes?.data) ? chungChiRes.data : [])).map(item => ({ ...item, type: 'chungChi' }));

            // Lọc bỏ Nhập liệu
            chungChiItems = chungChiItems.filter(item => {
                const status = item.trangThai ?? item.TrangThai;
                if (status === null || status === undefined) return true;
                if (!isNaN(status)) return Number(status) !== 0;
                return status !== "NhapLieu";
            });

            if ((selectedDonVi || filterChucDanh) && chungChiItems.length > 0) {
                const accDonViMap = {};
                const accChucDanhMap = {};

                try {
                    // TÌM DonViId và DoiTuongId từ danh sách options để truyền xuống API lọc
                    let selectedDonViId = null;
                    if (selectedDonVi) {
                        const dvList = Array.isArray(donViRes) ? donViRes : (donViRes?.data?.$values || donViRes?.data || []);
                        if (Array.isArray(dvList)) {
                            const foundDonVi = dvList.find(d => (d.tenDonVi || d.TenDonVi) === selectedDonVi);
                            if (foundDonVi) selectedDonViId = foundDonVi.id || foundDonVi.Id;
                        }
                    }

                    let selectedDoiTuongId = null;
                    if (filterChucDanh) {
                        const dtList = Array.isArray(chucDanhRes) ? chucDanhRes : (chucDanhRes?.data?.$values || chucDanhRes?.data || []);
                        if (Array.isArray(dtList)) {
                            const foundDt = dtList.find(d => (d.tenDoiTuong || d.TenDoiTuong) === filterChucDanh);
                            if (foundDt) selectedDoiTuongId = foundDt.id || foundDt.Id;
                        }
                    }

                    const payload = {
                        Keyword: "",
                        DonViId: selectedDonViId || "",
                        ChucDanhChuyenMonId: "",
                        DoiTuongId: selectedDoiTuongId || "",
                        PageIndex: 1,
                        PageSize: 1000000
                    };

                    // Gọi 1 lần lấy AccountInfo theo bộ lọc
                    const allRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, payload);
                    const allList = allRes?.data?.$values || allRes?.data?.data || allRes?.data || allRes || [];

                    if (Array.isArray(allList)) {
                        allList.forEach(info => {
                            const accId = info?.accountId || info?.AccountId;
                            if (!accId) return;
                            const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                            const tenDonVi = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : 'Chưa có đơn vị';
                            accDonViMap[accId] = tenDonVi;
                            accChucDanhMap[accId] = info?.doiTuong?.tenDoiTuong || info?.DoiTuong?.TenDoiTuong || '';
                        });
                    }
                } catch (e) {
                    console.error('[Filter] Lỗi khi lấy danh sách AccountInfo:', e);
                }

                chungChiItems = chungChiItems.filter(item => {
                    const accId = item.accountId || item.AccountId;
                    // Nếu account không có trong map (do API lọc theo DoiTuongId đã loại), bỏ qua luôn
                    if (!accDonViMap[accId] && !accChucDanhMap[accId]) return false;

                    let match = true;
                    const donVi = accDonViMap[accId] || 'Chưa có đơn vị';
                    const doiTuong = accChucDanhMap[accId] || '';

                    if (selectedDonVi) match = match && donVi === selectedDonVi;
                    if (filterChucDanh) match = match && doiTuong === filterChucDanh;
                    return match;
                });
            }

            // Nhóm theo AccountId trước
            const groupedMap = {};
            chungChiItems.forEach(item => {
                const accId = item.accountId || item.AccountId;
                if (!groupedMap[accId]) {
                    groupedMap[accId] = {
                        accountId: accId,
                        items: []
                    };
                }
                groupedMap[accId].items.push(item);
            });

            let uniqueAccIds = Object.keys(groupedMap);

            const totalRecords = (selectedDonVi || filterChucDanh || searchName)
                ? uniqueAccIds.length
                : (chungChiRes?.totalRecords || chungChiRes?.TotalRecords || chungChiRes?.totalRow || chungChiRes?.TotalRow || chungChiRes?.data?.totalRecords || chungChiRes?.data?.TotalRecords || 0);

            if (selectedDonVi || filterChucDanh || searchName) {
                uniqueAccIds = uniqueAccIds.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
            }

            // Fetch profile cho từng AccountId (đã phân trang)
            const profiles = await Promise.all(uniqueAccIds.map(async (accId) => {
                try {
                    const [authRes, detailRes] = await Promise.all([
                        Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${accId}`),
                        Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accId}`)
                    ]);

                    const auth = authRes?.data || authRes;
                    const detail = detailRes?.data || detailRes;

                    const fullName = auth ? [
                        auth.lastName || auth.LastName,
                        auth.middleName || auth.MiddleName,
                        auth.firstName || auth.FirstName
                    ].filter(v => v && String(v).trim() !== "").join(' ') : '-';

                    const dob = auth?.dateOfBirth || auth?.DateOfBirth;
                    const yearOfBirth = dob ? new Date(dob).getFullYear() : '-';
                    const chucDanh = detail?.doiTuong?.tenDoiTuong || detail?.DoiTuong?.TenDoiTuong || '-';
                    const donVis = detail?.donVis || detail?.DonVis?.$values || detail?.DonVis || [];
                    const khoa = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : '-';

                    return { accId, fullName, yearOfBirth, chucDanh, khoa };
                } catch (e) {
                    return { accId, fullName: '-', yearOfBirth: '-', chucDanh: '-', khoa: '-' };
                }
            }));

            // Trộn data profile vào Map
            profiles.forEach(p => {
                if (groupedMap[p.accId]) {
                    groupedMap[p.accId] = { ...groupedMap[p.accId], ...p };
                }
            });

            // Chỉ trả về các người nằm trong trang hiện tại
            const pagedItems = uniqueAccIds.map(accId => groupedMap[accId]);

            return {
                items: pagedItems,
                totalRecords: totalRecords
            };
        } catch (error) {
            console.error('Lỗi lấy dữ liệu duyệt:', error);
            throw error;
        }
    };

    const { data: listData, isLoading, refetch } = useQuery(
        ['duyetChungChiNgoaiNgu', currentPage, selectedDonVi, filterChucDanh, searchCccd, searchName],
        fetchAllData,
        { cacheTime: 0, staleTime: 0 }
    );

    const handleUpdateStatus = async (item, field, value) => {
        const queryKey = ['duyetChungChiNgoaiNgu', currentPage, selectedDonVi, searchCccd];
        const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;

        // Optimistic Update
        queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData) return { items: [], totalRecords: 0 };
            return {
                ...oldData,
                items: (oldData.items || []).map((employee) => ({
                    ...employee,
                    items: employee.items.map((it) => {
                        const itId = it.id || it.Id || it.vanBangId || it.chungChiNgoaiNguId;
                        if (itId === itemId) {
                            return {
                                ...it,
                                [field]: value,
                                [field.charAt(0).toLowerCase() + field.slice(1)]: value
                            };
                        }
                        return it;
                    })
                }))
            };
        });

        try {
            const apiPath = DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
            const payload = { id: itemId, fieldName: field, value: value };

            const res = await Put(apiPath, null, payload);
            if (res) {
                toast.success('Cập nhật trạng thái thành công');
                queryClient.invalidateQueries(queryKey);
                queryClient.invalidateQueries(['thongKeNgoaiNgu']);
            }
        } catch (error) {
            toast.error('Lỗi cập nhật trạng thái');
            refetch();
        }
    };

    const openConfirmAction = (item, field, value, label) => {
        setConfirmAction({ item, field, value, label });
        if ((field === 'TrangThai' && value === 3) || (field === 'XacNhanDatYeuCau' && value === 'Không đạt')) {
            setReasonText('');
            setOpenReasonModal(true);
        } else {
            setOpenConfirmDuyet(true);
        }
    };

    const handleReasonSubmit = async () => {
        if (!reasonText.trim()) {
            toast.error('Vui lòng nhập lý do không phù hợp');
            return;
        }
        if (!confirmAction) return;

        const { item, field, value } = confirmAction;
        setOpenReasonModal(false);

        await handleUpdateStatus(item, field, value);

        try {
            const apiPath = DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
            const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;
            const reasonFieldName = (field === 'TrangThai') ? 'LyDoKhongDuyet' : 'LyDoKhongDat';
            const payload = { id: itemId, fieldName: reasonFieldName, value: reasonText };
            await Put(apiPath, null, payload);
        } catch (error) {
            console.error('Lỗi cập nhật lý do:', error);
        }

        setConfirmAction(null);
    };

    const handleConfirmAccept = async () => {
        if (!confirmAction) return;
        const { item, field, value } = confirmAction;
        setOpenConfirmDuyet(false);

        await handleUpdateStatus(item, field, value);

        if ((field === 'TrangThai' && value === 2) || (field === 'XacNhanDatYeuCau' && value === 'Đạt')) {
            try {
                const apiPath = DANHSACH_DUYET_NGOAINGU_API.UPDATE_FIELD_CHUNG_CHI_NGOAI_NGU;
                const itemId = item.id || item.Id || item.vanBangId || item.chungChiNgoaiNguId;
                const reasonFieldName = (field === 'TrangThai') ? 'LyDoKhongDuyet' : 'LyDoKhongDat';
                const payload = { id: itemId, fieldName: reasonFieldName, value: null };
                await Put(apiPath, null, payload);
            } catch (error) {
                console.error('Lỗi reset lý do:', error);
            }
        }

        setConfirmAction(null);
    };

    const handleConfirmCancel = () => {
        setOpenConfirmDuyet(false);
        setConfirmAction(null);
    };

    const handleOpenFile = (path) => {
        if (!path) return;
        const fullPath = path.startsWith('http') ? path : `${BaseGatewayImage}${path}`;
        window.open(fullPath, '_blank');
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Đang tải dữ liệu và thông tin nhân viên...</p>
            </div>
        );
    }

    const totalRecords = listData?.totalRecords || 0;
    const totalPages = Math.ceil(totalRecords / itemsPerPage);
    const displayedEmployees = listData?.items || [];

    const handleExportExcel = async () => {
        try {
            setIsExporting(true);

            // 1. Fetch Account IDs to search if needed
            let accountIdToSearch = null;
            if (searchCccd) {
                const authRes = await Get(`${ACCOUNT_AUTHINFO_API.GET_BY_USERNAME}/${searchCccd}`);
                const authData = authRes?.data || authRes;
                accountIdToSearch = authData?.accountId || authData?.AccountId;
                if (!accountIdToSearch) {
                    toast.warning("Không tìm thấy dữ liệu để xuất Excel");
                    return;
                }
            }

            let params = { pageIndex: 1, pageSize: 1000000, keyword: "" };
            if (accountIdToSearch) {
                params.accountId = accountIdToSearch;
            }

            if (searchName) {
                const accountsRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT, null, { keyword: searchName, pageIndex: 1, pageSize: 1000000 });
                const accountsData = accountsRes?.data || accountsRes;
                const matchedAccounts = accountsData?.data || accountsData?.$values || (Array.isArray(accountsData) ? accountsData : []);
                const matchedIds = matchedAccounts.map(acc => acc.id || acc.Id).filter(Boolean);

                if (matchedIds.length === 0) {
                    toast.warning("Không tìm thấy dữ liệu để xuất Excel");
                    return;
                }
                params.AccountIds = matchedIds;
            }

            // 2. Fetch all certificates
            const chungChiRes = await Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_CHUNGCHINGOAINGU_BY_PAGING, null, params);
            let chungChiItems = (chungChiRes?.data?.$values || chungChiRes?.data?.data || (Array.isArray(chungChiRes?.data) ? chungChiRes.data : [])).map(item => ({ ...item, type: 'chungChi' }));

            // 3. Chỉ lấy những chứng chỉ đã duyệt
            chungChiItems = chungChiItems.filter(item => {
                const status = item.trangThai ?? item.TrangThai;
                if (status === null || status === undefined) return false;
                if (!isNaN(status)) return Number(status) === 2;
                return String(status).trim() === "DaDuyet";
            });

            // 4. Lọc theo đơn vị, chức danh
            if ((selectedDonVi || filterChucDanh) && chungChiItems.length > 0) {
                const accDonViMap = {};
                const accChucDanhMap = {};

                try {
                    let selectedDonViId = null;
                    if (selectedDonVi) {
                        const dvList = Array.isArray(donViRes) ? donViRes : (donViRes?.data?.$values || donViRes?.data || []);
                        if (Array.isArray(dvList)) {
                            const foundDonVi = dvList.find(d => (d.tenDonVi || d.TenDonVi) === selectedDonVi);
                            if (foundDonVi) selectedDonViId = foundDonVi.id || foundDonVi.Id;
                        }
                    }

                    let selectedDoiTuongId = null;
                    if (filterChucDanh) {
                        const dtList = Array.isArray(chucDanhRes) ? chucDanhRes : (chucDanhRes?.data?.$values || chucDanhRes?.data || []);
                        if (Array.isArray(dtList)) {
                            const foundDt = dtList.find(d => (d.tenDoiTuong || d.TenDoiTuong) === filterChucDanh);
                            if (foundDt) selectedDoiTuongId = foundDt.id || foundDt.Id;
                        }
                    }

                    const payload = {
                        Keyword: "",
                        DonViId: selectedDonViId || "",
                        ChucDanhChuyenMonId: "",
                        DoiTuongId: selectedDoiTuongId || "",
                        PageIndex: 1,
                        PageSize: 1000000
                    };

                    const allRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, payload);
                    const allList = allRes?.data?.$values || allRes?.data?.data || allRes?.data || allRes || [];

                    if (Array.isArray(allList)) {
                        allList.forEach(info => {
                            const accId = info?.accountId || info?.AccountId;
                            if (!accId) return;
                            const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                            const tenDonVi = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : 'Chưa có đơn vị';
                            accDonViMap[accId] = tenDonVi;
                            accChucDanhMap[accId] = info?.doiTuong?.tenDoiTuong || info?.DoiTuong?.TenDoiTuong || '';
                        });
                    }
                } catch (e) {
                    console.error('[Export Filter] Lỗi khi lấy danh sách AccountInfo:', e);
                }

                chungChiItems = chungChiItems.filter(item => {
                    const accId = item.accountId || item.AccountId;
                    if (!accDonViMap[accId] && !accChucDanhMap[accId]) return false;

                    let match = true;
                    const donVi = accDonViMap[accId] || 'Chưa có đơn vị';
                    const doiTuong = accChucDanhMap[accId] || '';

                    if (selectedDonVi) match = match && donVi === selectedDonVi;
                    if (filterChucDanh) match = match && doiTuong === filterChucDanh;
                    return match;
                });
            }

            if (chungChiItems.length === 0) {
                toast.warning("Không có dữ liệu để xuất Excel");
                return;
            }

            // 5. Group theo AccountId
            const groupedMap = {};
            chungChiItems.forEach(item => {
                const accId = item.accountId || item.AccountId;
                if (!groupedMap[accId]) {
                    groupedMap[accId] = {
                        accountId: accId,
                        items: []
                    };
                }
                groupedMap[accId].items.push(item);
            });

            const uniqueAccIds = Object.keys(groupedMap);

            // 6. Fetch bulk data (chỉ 2 API calls thay vì N*2)
            // 6a. Lấy ALL AccountAuthInfo (để lấy họ tên, năm sinh) - 1 API call
            const accountAuthMap = new Map();
            try {
                const authRes = await Get(ACCOUNT_AUTHINFO_API.GET_PAGING, null, { pageIndex: 1, pageSize: 1000000 });
                const authData = authRes?.data?.data || authRes?.data?.$values || authRes?.data || authRes || [];
                const authList = Array.isArray(authData) ? authData : [];
                authList.forEach(acc => {
                    const accId = acc.accountId || acc.AccountId || acc.id || acc.Id;
                    if (accId) {
                        const dob = acc.dateOfBirth || acc.DateOfBirth;
                        accountAuthMap.set(accId, {
                            lastName: acc.lastName || acc.LastName || "",
                            middleName: acc.middleName || acc.MiddleName || "",
                            firstName: acc.firstName || acc.FirstName || "",
                            yearOfBirth: dob ? new Date(dob).getFullYear() : '-'
                        });
                    }
                });
            } catch (e) {
                console.error('[Export] Lỗi khi fetch bulk AccountAuthInfo:', e);
            }

            // 6b. Lấy ALL AccountInfo (để lấy chức danh, đơn vị/khoa) - 1 API call
            const accountInfoMap = new Map();
            try {
                const infoRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, { Keyword: "", PageIndex: 1, PageSize: 1000000 });
                const infoData = infoRes?.data?.$values || infoRes?.data || infoRes || [];
                const infoList = Array.isArray(infoData) ? infoData : [];
                infoList.forEach(info => {
                    const accId = info?.accountId || info?.AccountId;
                    if (accId) {
                        const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                        const khoa = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : '-';
                        accountInfoMap.set(accId, {
                            chucDanh: info?.doiTuong?.tenDoiTuong || info?.DoiTuong?.TenDoiTuong || '-',
                            khoa: khoa
                        });
                    }
                });
            } catch (e) {
                console.error('[Export] Lỗi khi fetch bulk AccountInfo:', e);
            }

            // 6c. Map profile vào groupedMap từ Maps (KHÔNG gọi API nào nữa)
            uniqueAccIds.forEach(accId => {
                if (groupedMap[accId]) {
                    const authData = accountAuthMap.get(accId);
                    const infoData = accountInfoMap.get(accId);

                    const fullName = authData
                        ? [authData.lastName, authData.middleName, authData.firstName].filter(v => v && String(v).trim() !== "").join(' ')
                        : '-';
                    const yearOfBirth = authData?.yearOfBirth || '-';
                    const chucDanh = infoData?.chucDanh || '-';
                    const khoa = infoData?.khoa || '-';

                    groupedMap[accId] = { ...groupedMap[accId], accId, fullName, yearOfBirth, chucDanh, khoa };
                }
            });

            const exportData = Object.values(groupedMap);

            // 7. Tạo file Excel với thẻ TD rowspan
            let tableHTML = `
                <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                <head>
                    <meta charset="UTF-8">
                    <!--[if gte mso 9]>
                    <xml>
                        <x:ExcelWorkbook>
                            <x:ExcelWorksheets>
                                <x:ExcelWorksheet>
                                    <x:Name>DanhSachNgoaiNgu</x:Name>
                                    <x:WorksheetOptions>
                                        <x:DisplayGridlines/>
                                    </x:WorksheetOptions>
                                </x:ExcelWorksheet>
                            </x:ExcelWorksheets>
                        </x:ExcelWorkbook>
                    </xml>
                    <![endif]-->
                </head>
                <body>
                    <table border="1">
                        <thead>
                            <tr>
                                <th style="background-color: #f2f2f2; text-align: center;">STT</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Họ và tên</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Năm sinh</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Chức danh</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Khoa/Phòng</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Tên văn bằng/chứng chỉ</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Loại ngoại ngữ</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Trình độ</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Điểm số</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Ngày cấp</th>
                                <th style="background-color: #f2f2f2; text-align: center;">Ngày hết hạn</th>
                            </tr>
                        </thead>
                        <tbody>
            `;

            exportData.forEach((emp, index) => {
                const stt = index + 1;
                const rowSpan = emp.items.length;
                emp.items.forEach((item, itemIdx) => {
                    tableHTML += `<tr>`;
                    if (itemIdx === 0) {
                        tableHTML += `
                            <td rowspan="${rowSpan}" style="vertical-align: middle; text-align: center;">${stt}</td>
                            <td rowspan="${rowSpan}" style="vertical-align: middle;">${emp.fullName || '-'}</td>
                            <td rowspan="${rowSpan}" style="vertical-align: middle; text-align: center;">${emp.yearOfBirth || '-'}</td>
                            <td rowspan="${rowSpan}" style="vertical-align: middle;">${emp.chucDanh || '-'}</td>
                            <td rowspan="${rowSpan}" style="vertical-align: middle;">${emp.khoa || '-'}</td>
                        `;
                    }
                    tableHTML += `
                            <td style="vertical-align: middle;">${item.tenChungChi || item.TenChungChi || '-'}</td>
                            <td style="vertical-align: middle; text-align: center;">${item.loaiNgoaiNgu || item.LoaiNgoaiNgu || '-'}</td>
                            <td style="vertical-align: middle; text-align: center;">${item.trinhDo || item.TrinhDo || '-'}</td>
                            <td style="vertical-align: middle; text-align: center;">${formatDiemSo(item.diemSo || item.DiemSo)}</td>
                            <td style="vertical-align: middle; text-align: center;">${formatDate(item.ngayCap || item.NgayCap)}</td>
                            <td style="vertical-align: middle; text-align: center;">${formatDate(item.ngayHetHan || item.NgayHetHan)}</td>
                        </tr>
                    `;
                });
            });

            tableHTML += `
                        </tbody>
                    </table>
                </body>
                </html>
            `;

            const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `DanhSachDuyetNgoaiNgu_${Date.now()}.xls`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success('Xuất Excel thành công');

        } catch (error) {
            console.error('Lỗi khi xuất excel:', error);
            toast.error('Có lỗi xảy ra khi xuất Excel');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="py-2">
            {/* Bộ lọc đơn vị */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3">
                    <h5 className="fw-bold text-dark mb-0">Danh sách chứng chỉ chờ duyệt</h5>
                    <Button
                        variant="success"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={isExporting}
                        className="d-flex align-items-center gap-2 shadow-sm"
                    >
                        {isExporting ? <Spinner animation="border" size="sm" /> : <i className="fas fa-file-excel"></i>}
                        {isExporting ? 'Đang xuất...' : 'Xuất Excel'}
                    </Button>
                </div>
                <div className="d-flex align-items-center justify-content-end gap-3 flex-wrap ms-auto">

                    {/* Ô TÌM KIẾM THEO CCCD */}
                    <div className="d-flex flex-column" style={{ minWidth: 260 }}>
                        <span className="fw-semibold text-muted mb-1" style={{ fontSize: '0.85rem' }}>Tìm theo CCCD:</span>
                        <div className="d-flex w-100">
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Nhập số CCCD..."
                                value={searchCccdInput}
                                onChange={(e) => setSearchCccdInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearchCccd(searchCccdInput);
                                        setCurrentPage(1);
                                    }
                                }}
                                style={{ borderRadius: '8px 0 0 8px', fontSize: '0.9rem', height: '38px', borderRight: 'none' }}
                            />
                            <Button
                                size="sm"
                                variant="primary"
                                style={{ borderRadius: '0 8px 8px 0', height: '38px', px: 3 }}
                                onClick={() => {
                                    setSearchCccd(searchCccdInput);
                                    setCurrentPage(1);
                                }}
                            >
                                <i className="fas fa-search"></i>
                            </Button>
                        </div>
                    </div>

                    {/* TÌM THEO TÊN */}
                    <div className="d-flex flex-column" style={{ minWidth: 260 }}>
                        <span className="fw-semibold text-muted mb-1" style={{ fontSize: '0.85rem' }}>Tìm theo Tên:</span>
                        <div className="d-flex w-100">
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Nhập tên..."
                                value={searchNameInput}
                                onChange={(e) => setSearchNameInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearchName(searchNameInput);
                                        setCurrentPage(1);
                                    }
                                }}
                                style={{ borderRadius: '8px 0 0 8px', fontSize: '0.9rem', height: '38px', borderRight: 'none' }}
                            />
                            <Button
                                size="sm"
                                variant="primary"
                                style={{ borderRadius: '0 8px 8px 0', height: '38px', paddingLeft: '15px', paddingRight: '15px' }}
                                onClick={() => {
                                    setSearchName(searchNameInput);
                                    setCurrentPage(1);
                                }}
                            >
                                <i className="fas fa-search"></i>
                            </Button>
                        </div>
                    </div>
                    <div className="d-flex flex-column" style={{ minWidth: 260 }}>
                        <span className="fw-semibold text-muted mb-1" style={{ fontSize: '0.85rem' }}>Lọc theo đơn vị:</span>
                        <div className="w-100">
                            <Select
                                options={donViOptions}
                                value={selectedDonVi ? { value: selectedDonVi, label: selectedDonVi } : null}
                                onChange={(opt) => {
                                    setSelectedDonVi(opt ? opt.value : '');
                                    setCurrentPage(1);
                                }}
                                placeholder="-- Tất cả đơn vị --"
                                isClearable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: 8,
                                        fontSize: '0.9rem',
                                        border: '1px solid #dee2e6',
                                        boxShadow: 'none',
                                        height: '38px',
                                        '&:hover': {
                                            border: '1px solid #ced4da'
                                        }
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        fontSize: '0.9rem',
                                        zIndex: 999
                                    })
                                }}
                            />
                        </div>
                    </div>

                    {/* BỘ LỌC CHỨC DANH */}
                    <div className="d-flex flex-column" style={{ minWidth: 260 }}>
                        <span className="fw-semibold text-muted mb-1" style={{ fontSize: '0.85rem' }}>Lọc theo chức danh:</span>
                        <div className="w-100">
                            <Select
                                options={chucDanhOptions}
                                value={filterChucDanh ? { value: filterChucDanh, label: filterChucDanh } : null}
                                onChange={(opt) => {
                                    setFilterChucDanh(opt ? opt.value : '');
                                    setCurrentPage(1);
                                }}
                                placeholder="-- Tất cả chức danh --"
                                isClearable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: 8,
                                        fontSize: '0.9rem',
                                        border: '1px solid #dee2e6',
                                        boxShadow: 'none',
                                        height: '38px',
                                        '&:hover': {
                                            border: '1px solid #ced4da'
                                        }
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        fontSize: '0.9rem',
                                        zIndex: 999
                                    })
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <Table bordered className="align-middle custom-approval-table">
                    <thead className="table-light">
                        <tr className="text-center text-nowrap">
                            <th style={{ width: '50px' }}>STT</th>
                            <th style={{ minWidth: '180px' }}>Họ và tên</th>
                            <th style={{ width: '100px' }}>Năm sinh</th>
                            <th style={{ minWidth: '110px' }}>Chức danh</th>
                            <th style={{ minWidth: '180px' }}>Đơn vị</th>
                            <th style={{ minWidth: '120px' }}>Số chứng chỉ</th>
                            <th style={{ minWidth: '160px' }}>Tên chứng chỉ</th>
                            <th style={{ minWidth: '120px' }}>Loại ngoại ngữ</th>
                            <th style={{ minWidth: '100px' }}>Trình độ</th>
                            <th style={{ minWidth: '90px' }}>Điểm số</th>
                            <th style={{ minWidth: '120px' }}>Ngày cấp</th>
                            <th style={{ minWidth: '120px' }}>Ngày hết hạn</th>
                            <th style={{ minWidth: '140px' }}>Đơn vị cấp</th>
                            <th style={{ minWidth: '120px' }}>Quốc gia cấp</th>
                            <th style={{ minWidth: '100px' }}>Minh chứng</th>
                            <th style={{ minWidth: '140px' }}>Duyệt chứng chỉ</th>
                            <th style={{ minWidth: '140px' }}>Xác nhận Đạt yêu cầu</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedEmployees.length > 0 ? displayedEmployees.map((employee, index) => {
                            const stt = (currentPage - 1) * itemsPerPage + index + 1;
                            const rowSpan = employee.items.length;

                            return (
                                <React.Fragment key={employee.accountId || index}>
                                    {employee.items.map((item, idx) => {
                                        const tenHienThi = item.tenChungChi || item.TenChungChi || '-';
                                        const filePath = item.fileMinhChungPath || item.FileMinhChungPath;

                                        const parseTrangThai = (val) => {
                                            if (val === null || val === undefined) return -1;
                                            if (!isNaN(val)) return Number(val);
                                            const enumMap = { "NhapLieu": 0, "ChoDuyet": 1, "DaDuyet": 2, "TuChoi": 3 };
                                            return enumMap[String(val).trim()] ?? -1;
                                        };

                                        const statusDuyet = parseTrangThai(item.trangThai ?? item.TrangThai);
                                        const statusDat = item.xacNhanDatYeuCau ?? item.XacNhanDatYeuCau ?? null;

                                        return (
                                            <tr key={item.id || `${employee.accId}-${idx}`}>
                                                {idx === 0 && (
                                                    <>
                                                        <td rowSpan={rowSpan} className="text-center" style={{ color: '#000' }}>{stt}</td>
                                                        <td rowSpan={rowSpan} className="fw-bold text-primary" style={{ color: '#007bff' }}>{employee.fullName || '-'}</td>
                                                        <td rowSpan={rowSpan} className="text-center" style={{ color: '#000' }}>{employee.yearOfBirth || '-'}</td>
                                                        <td rowSpan={rowSpan} style={{ color: '#000' }}>{employee.chucDanh || '-'}</td>
                                                        <td rowSpan={rowSpan} style={{ color: '#000' }}>{employee.khoa || '-'}</td>
                                                    </>
                                                )}
                                                <td className="text-center" style={{ color: '#000' }}>{item.soChungChi || item.SoChungChi || '-'}</td>
                                                <td style={{ color: '#000' }}>{item.tenChungChi || item.TenChungChi || '-'}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{item.loaiNgoaiNgu || item.LoaiNgoaiNgu || '-'}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{item.trinhDo || item.TrinhDo || '-'}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{formatDiemSo(item.diemSo || item.DiemSo)}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{formatDate(item.ngayCap || item.NgayCap)}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{formatDate(item.ngayHetHan || item.NgayHetHan)}</td>
                                                <td style={{ color: '#000' }}>{item.donViCap || item.DonViCap || '-'}</td>
                                                <td className="text-center" style={{ color: '#000' }}>{item.quocGiaCap || item.QuocGiaCap || '-'}</td>
                                                <td className="text-center">
                                                    {filePath ? (
                                                        <Button variant="outline-info" size="sm" onClick={() => handleOpenFile(filePath)}>
                                                            Xem
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Button variant={statusDuyet === 2 ? 'success' : 'outline-success'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'TrangThai', 2, `Xác nhận duyệt "${tenHienThi}"?`)} title="Duyệt">
                                                            <i className="fas fa-check"></i>
                                                        </Button>
                                                        <Button variant={statusDuyet === 3 ? 'danger' : 'outline-danger'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'TrangThai', 3, `Xác nhận không duyệt "${tenHienThi}"?`)} title="Không duyệt">
                                                            <i className="fas fa-times"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td className="text-center" style={{ color: '#000' }}>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <Button variant={(statusDat === 'Đạt') && statusDat !== null ? 'success' : 'outline-success'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'XacNhanDatYeuCau', 'Đạt', 'Xác nhận đạt yêu cầu?')} title="Đạt">
                                                            <i className="fas fa-check"></i>
                                                        </Button>
                                                        <Button variant={(statusDat === 'Không đạt') && statusDat !== null ? 'danger' : 'outline-danger'} size="sm" className="btn-icon-square"
                                                            onClick={() => openConfirmAction(item, 'XacNhanDatYeuCau', 'Không đạt', 'Xác nhận không đạt yêu cầu?')} title="Không đạt">
                                                            <i className="fas fa-times"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        }) : (
                            <tr><td colSpan="17" className="text-center py-4 text-muted">Không có dữ liệu chờ duyệt</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination Component đã được sửa */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-3">
                    <Pagination className="custom-pagination">
                        <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />

                        {/* Thay đổi đoạn này */}
                        {getPaginationItems(currentPage, totalPages).map((item, idx) => (
                            item === '...' ? (
                                <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                            ) : (
                                <Pagination.Item
                                    key={`page-${item}`}
                                    active={item === currentPage}
                                    onClick={() => setCurrentPage(item)}
                                >
                                    {item}
                                </Pagination.Item>
                            )
                        ))}

                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                        <Pagination.Last disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} />
                    </Pagination>
                </div>
            )}

            <ConfirmModal isOpen={openConfirmDuyet} confirmType="confirm" handleAccept={handleConfirmAccept} handleCancel={handleConfirmCancel}
                content={confirmAction?.label || 'Bạn có chắc chắn muốn thực hiện thao tác này?'} />

            {/* Modal Lý do không phù hợp */}
            <Modal
                show={openReasonModal}
                onHide={() => setOpenReasonModal(false)}
                centered
                className="reason-modal"
                style={{ zIndex: 1060 }}
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="d-flex align-items-center text-danger fw-bold h5">
                        <i className="fas fa-exclamation-triangle me-2"></i> Lý do không phù hợp
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form.Group>
                        <Form.Label className="fw-bold">Chọn lý do <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            as="input"
                            placeholder="-- Vui lòng nhập lý do --"
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            className="py-2"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-end pt-0">
                    <Button variant="light" onClick={() => setOpenReasonModal(false)} className="px-4 border">
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={handleReasonSubmit} className="px-4 bg-primary border-0" style={{ background: '#58a5ff' }}>
                        Tiếp tục
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .custom-approval-table { border: 1px solid #dee2e6; font-size: 0.9rem; }
                .custom-approval-table thead th { background-color: #f8f9fa; color: #495057; font-weight: 700; border: 1px solid #dee2e6 !important; border-bottom: 2px solid #dee2e6 !important; padding: 12px 8px; text-align: center; }
                .custom-approval-table tbody td { padding: 12px 8px; vertical-align: middle; text-align: center; background-color: #fff; border: 1px solid #dee2e6; }
                .btn-icon-square { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; padding: 0; border-radius: 3px; }
                
                .reason-modal .modal-content { border-radius: 12px; border: none; box-shadow: 0 5px 15px rgba(0,0,0,.2); }
                .reason-modal .modal-header .btn-close { font-size: 0.8rem; }
                .reason-modal .form-control:focus { box-shadow: none; border-color: #dee2e6; }
                
                /* Pagination styling */
                .custom-pagination .page-link {
                    color: #0d6efd;
                    border-radius: 6px;
                    margin: 0 3px;
                    border: 1px solid #dee2e6;
                    padding: 6px 12px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                .custom-pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: #fff;
                }
                .custom-pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    background-color: #fff;
                    border-color: #dee2e6;
                }
                .custom-pagination .page-link:hover {
                    background-color: #e9ecef;
                    border-color: #dee2e6;
                }

                .modal-open .modal { z-index: 1060 !important; }
                .modal-backdrop { z-index: 1050 !important; }
            `}</style>
        </div>
    );
};

export default DuyetChungChiNgoaiNgu;